import {
  Box,
  Card,
  CardContent,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import React, { useMemo, useState } from 'react'
import { Trans } from 'react-i18next'
import {
  Button,
  IconButton,
  Icons,
  InformationModal,
  SkeletonText,
} from 'utoniq-ui'

import {
  Maybe,
  TicketItemMetadataAssignedNumberType,
  TicketShareSelectUserItemSerialFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { useBrowserLink } from '@/components/lib/BrowserLink'
import { getSerialFormatNumber, TicketLabel } from '@/components/organisms'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { getHelpUrl } from '@/lib/helpUrl'

export * from './fragments'

export type State = {
  loading: boolean
  generating: boolean
  regenerating: boolean
  artistId: string
  throughTicketIndex?: string
  assignedNumberType: Maybe<TicketItemMetadataAssignedNumberType>
  serial: Maybe<TicketShareSelectUserItemSerialFragment>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onGenerateTicketDistribution: () => void
  onRegenerateTicketDistribution: () => void
}

export const TicketShareSelectTemplate: Template<State, Action> = ({
  state,
  onGenerateTicketDistribution,
  onRegenerateTicketDistribution,
}) => {
  const { t } = useTranslation('ticketShareSelect')
  const { routerNames, router } = useRouter()
  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <BaseLayout
        margin
        header={{
          title: t('title', 'チケットの分配'),
          right: (
            <IconButton.Close edge="end" onClick={() => router.goBack()} />
          ),
        }}
      >
        <Head title={routerNames.TicketShareSelect} />
        {state.loading ? (
          <TicketShareSelectSkeleton />
        ) : (
          <TicketShareSelectCell
            serial={state.serial}
            assignedNumberType={state.assignedNumberType}
            generating={state.generating}
            throughTicketIndex={state.throughTicketIndex}
            regenerating={state.regenerating}
            onGenerateTicketDistribution={onGenerateTicketDistribution}
            onRegenerateTicketDistribution={onRegenerateTicketDistribution}
          />
        )}
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

type TicketShareSelectCellProps = {
  generating: State['generating']
  regenerating: State['regenerating']
  throughTicketIndex: State['throughTicketIndex']
  serial: State['serial']
  assignedNumberType: State['assignedNumberType']
  onGenerateTicketDistribution: Action['onGenerateTicketDistribution']
  onRegenerateTicketDistribution: Action['onRegenerateTicketDistribution']
}
const TicketShareSelectCell: Cell<TicketShareSelectCellProps> = ({
  generating,
  regenerating,
  throughTicketIndex,
  serial,
  assignedNumberType,
  onGenerateTicketDistribution,
  onRegenerateTicketDistribution,
}) => {
  const { t } = useTranslation('ticketShareSelect')
  const [openRegenerate, setOpenRegenerate] = useState<boolean>(false)
  const { onClick } = useBrowserLink(getHelpUrl('ticketDistribution'))
  const isExpired = !!serial?.serial?.ticketDistributionLink?.isExpired

  const ticketNumber = useMemo(() => {
    return getSerialFormatNumber(serial?.serial, throughTicketIndex)
  }, [serial?.serial, throughTicketIndex])

  return (
    <CellFrame sx={{ py: 5 }}>
      {isExpired ? (
        <Stack
          spacing={1}
          sx={{
            p: 3,
            borderColor: 'error.main',
            borderWidth: 1,
            borderStyle: 'solid',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Icons.Info fontSize="small" sx={{ color: 'error.main' }} />
            <Typography variant="subtitle2">
              {t('expiredTitle', '分配URL有効期限切れ')}
            </Typography>
          </Stack>
          <Typography variant="caption">
            <Trans
              i18nKey="expiredNote"
              t={t}
              components={{
                linkTag: (
                  <Typography
                    component="span"
                    variant="caption"
                    color="primary"
                    sx={{ cursor: 'pointer' }}
                    onClick={() => setOpenRegenerate(true)}
                  />
                ),
              }}
            />
          </Typography>
          <Typography
            variant="caption"
            color="primary.main"
            sx={{ cursor: 'pointer' }}
            onClick={onClick}
          >
            {t('getHelp', 'ヘルプを見る')}
          </Typography>
        </Stack>
      ) : (
        <Typography variant="body2">{t('description')}</Typography>
      )}
      <Stack spacing={2} pt={3}>
        <TicketLabel
          serial={serial?.serial}
          assignedNumberType={assignedNumberType}
        />
        <Card elevation={0} sx={{ borderRadius: '6px' }}>
          <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
            <Typography variant="subtitle1">{ticketNumber}</Typography>
          </CardContent>
        </Card>
      </Stack>
      {!isExpired && (
        <Typography variant="caption" component="p" pt={3}>
          {t('note')}
        </Typography>
      )}
      <Stack pt={5} alignItems="center">
        {isExpired ? (
          <Button
            size="large"
            variant="outlined"
            loading={generating}
            sx={{ width: 244 }}
            onClick={() => setOpenRegenerate(true)}
          >
            {t('reissueDistribution')}
          </Button>
        ) : (
          <Button
            size="large"
            loading={generating}
            sx={{ width: 244 }}
            onClick={onGenerateTicketDistribution}
          >
            {t('issueDistribution')}
          </Button>
        )}
      </Stack>
      <InformationModal
        open={openRegenerate}
        onClose={() => setOpenRegenerate(false)}
        title={
          <Typography variant="h6" sx={{ pb: 3 }}>
            {t('reissueTitle')}
          </Typography>
        }
        content=""
        cancelText={t('close')}
        buttons={
          <Button
            loading={regenerating}
            size="large"
            fullWidth
            onClick={onRegenerateTicketDistribution}
          >
            {t('reissueConfirm')}
          </Button>
        }
      />
    </CellFrame>
  )
}

const TicketShareSelectSkeleton: Cell = () => (
  <CellFrame sx={{ py: 5 }}>
    <Box sx={{ py: 2 }}>
      <Stack spacing={1}>
        <SkeletonText variant="body2" width="100%" />
        <SkeletonText variant="body2" width="50%" />
      </Stack>
    </Box>
    <Card elevation={0} sx={{ borderRadius: 0, mt: 5, bgcolor: 'grey.50' }}>
      <CardContent sx={{ px: 2, py: 3, '&:last-child': { pb: 3 } }}>
        <Stack spacing={2}>
          <SkeletonText variant="body2" width="100%" />
          <SkeletonText variant="body2" width="50%" />
        </Stack>
      </CardContent>
    </Card>
    <Stack spacing={1} mt={5}>
      <SkeletonText variant="body2" width="100%" />
      <SkeletonText variant="body2" width="50%" />
    </Stack>
    <Stack mt={5} p={2} alignItems="center">
      <Skeleton
        variant="rectangular"
        height={44}
        width={244}
        sx={{ borderRadius: '34px' }}
      />
    </Stack>
  </CellFrame>
)
